import { createTheme } from '@mui/material/styles';
import { createSAPTheme, sapTheme, createSAPThemeVariant } from './theme/sapTheme';

// ============================================================================
// ENTERPRISE THEME - SINGLE SOURCE OF TRUTH
// ============================================================================
// This theme replaces the SAP design system tokens and styled components
// All components should use this theme via sx props or theme object
// Now using the unified SAP theme integration
// ============================================================================

// SAP Fiori 3.0 Color Palette (migrated from sapDesignSystem.ts)
const SAP_COLORS = {
  // Primary Brand Colors
  sapBlue: '#0070F2',           // Primary actions, links, selected states
  sapBlueDark: '#002C5F',       // Headers, emphasis text
  sapDarkBlue: '#002C5F',       // Headers, emphasis text
  sapLightBlue: '#D1E8FF',      // Background hover, light selections
  sapPaleBlue: '#F5FAFF',       // Background areas, panels
  
  // Semantic Colors
  sapGreen: '#30914C',          // Success states, positive actions
  sapLightGreen: '#E5F3E9',     // Success backgrounds
  sapRed: '#CC1919',            // Error states, critical actions
  sapLightRed: '#FEECEE',       // Error backgrounds
  sapOrange: '#E76500',         // Warning states, attention
  sapLightOrange: '#FEF2E6',    // Warning backgrounds
  sapYellow: '#FFAB1D',         // Information states, highlights
  sapLightYellow: '#FFF8E6',    // Information backgrounds
  
  // Neutral Palette
  sapGray1: '#F7F7F7',          // Page background
  sapGray2: '#EDEDED',          // Card background, borders
  sapGray3: '#D9D9D9',          // Dividers, inactive borders
  sapGray4: '#BFBFBF',          // Disabled text, placeholders
  sapGray5: '#89919A',          // Secondary text, labels
  sapGray6: '#5A6270',          // Body text
  sapGray7: '#32363A',          // Primary text, headers
  sapBlack: '#000000',          // High contrast text
  sapWhite: '#FFFFFF',          // Pure white backgrounds
  
  // Interactive States
  hover: '#E6F3FF',             // Hover backgrounds
  active: '#CCE5FF',            // Active/pressed states
  focus: '#0070F2',             // Focus indicators
  disabled: '#F2F2F2',         // Disabled backgrounds
  
  // Chart Colors (for dashboards)
  chart1: '#5899DA',
  chart2: '#E8743B',
  chart3: '#19A979',
  chart4: '#ED4A7B',
  chart5: '#945ECF',
  chart6: '#13A4B4',
  chart7: '#525DF4',
  chart8: '#BF399E',
} as const;

// SAP Spacing System (8px base grid)
const SAP_SPACING = {
  xs: '0.25rem',    // 4px - Tight spacing
  sm: '0.5rem',     // 8px - Base unit
  md: '1rem',       // 16px - Standard spacing
  lg: '1.5rem',     // 24px - Section spacing
  xl: '2rem',       // 32px - Large spacing
  '2xl': '3rem',    // 48px - Extra large spacing
  '3xl': '4rem',    // 64px - Page sections
  '4xl': '6rem',    // 96px - Hero sections
  '5xl': '8rem',    // 128px - Extra large sections
  '6xl': '12rem',   // 192px - Maximum spacing
} as const;

// SAP Shadows (Elevation system)
const SAP_SHADOWS = {
  none: 'none',
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  card: '0 2px 8px 0 rgba(0, 0, 0, 0.1)',
  modal: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
} as const;

// SAP Fiori 3.0 Enhanced Color Palette
const colors = {
  // Primary SAP brand colors
  primary: SAP_COLORS.sapBlue,
  primaryDark: SAP_COLORS.sapDarkBlue,
  primaryLight: SAP_COLORS.sapLightBlue,
  primaryGradient: `linear-gradient(135deg, ${SAP_COLORS.sapBlue} 0%, ${SAP_COLORS.sapDarkBlue} 100%)`,
  
  // Secondary colors - SAP neutral palette
  secondary: SAP_COLORS.sapGray2,
  secondaryLight: SAP_COLORS.sapGray1,
  secondaryGradient: `linear-gradient(135deg, ${SAP_COLORS.sapGray1} 0%, ${SAP_COLORS.sapGray2} 100%)`,
  
  // Text colors - SAP typography scale
  textPrimary: SAP_COLORS.sapGray7,
  textSecondary: SAP_COLORS.sapGray5,
  textLight: SAP_COLORS.sapGray4,
  textWhite: SAP_COLORS.sapWhite,
  
  // Background colors - SAP background system
  background: SAP_COLORS.sapWhite,
  backgroundLight: SAP_COLORS.sapGray1,
  backgroundGradient: `linear-gradient(135deg, ${SAP_COLORS.sapGray1} 0%, ${SAP_COLORS.sapGray2} 100%)`,
  paperLight: SAP_COLORS.sapWhite,
  paperGradient: `linear-gradient(135deg, ${SAP_COLORS.sapWhite} 0%, ${SAP_COLORS.sapGray1} 100%)`,
  
  // Border colors - SAP border system
  border: SAP_COLORS.sapGray3,
  borderLight: SAP_COLORS.sapGray2,
  
  // Status colors - SAP semantic colors
  success: SAP_COLORS.sapGreen,
  error: SAP_COLORS.sapRed,
  warning: SAP_COLORS.sapOrange,
  info: SAP_COLORS.sapBlue,
  
  // Hover states - SAP interactive colors
  hoverLight: SAP_COLORS.hover,
  hoverPrimary: SAP_COLORS.sapLightBlue,
  hoverGradient: `linear-gradient(135deg, ${SAP_COLORS.hover} 0%, ${SAP_COLORS.sapLightBlue} 100%)`,
  
  // Shadows - SAP elevation system
  shadowLight: SAP_SHADOWS.sm,
  shadowMedium: SAP_SHADOWS.md,
  shadowCard: SAP_SHADOWS.card,
  shadowLarge: SAP_SHADOWS.lg,
  shadowXLarge: SAP_SHADOWS.xl,
};

// Unified spacing system
const spacing = {
  xs: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 6,
  '2xl': 8,
  '3xl': 12,
};

// Unified border radius
const borderRadius = {
  small: 8,
  medium: 12,
  large: 24,
  card: 3,
  xl: 32,
};

// Use the unified SAP theme as the default light theme
export const lightTheme = sapTheme;

// Dark theme using SAP theme variant
export const darkTheme = createSAPThemeVariant('dark');

// ============================================================================
// EXPORTED DESIGN SYSTEM - FOR COMPONENT USAGE
// ============================================================================
// Use these values in sx props instead of importing from sapDesignSystem.ts
// Example: sx={{ color: 'text.primary', p: 3 }} instead of SAP_COLORS.sapGray7

export const designSystem = {
  colors,
  spacing,
  borderRadius,
};

// ============================================================================
// MIGRATION HELPERS - TEMPORARY EXPORTS
// ============================================================================
// These exports help with gradual migration from SAP tokens to theme values
// TODO: Remove these after all components are migrated

export { SAP_COLORS, SAP_SPACING, SAP_SHADOWS };

// ============================================================================
// USAGE EXAMPLES
// ============================================================================
// 
// BEFORE (Old approach):
// import { SAP_COLORS, SAP_SPACING } from '../theme/sapDesignSystem';
// sx={{ color: SAP_COLORS.sapGray7, p: SAP_SPACING.lg }}
//
// AFTER (New approach):
// sx={{ color: 'text.primary', p: 3 }}
//
// AVAILABLE THEME VALUES:
// - Colors: 'primary', 'secondary', 'text.primary', 'text.secondary', 'background.paper', etc.
// - Spacing: 1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24 (8px grid)
// - Shadows: 0-24 (Material-UI elevation system)
// - Border radius: 1, 2, 3, 4, 6, 8, 12, 16, 24
//
// ============================================================================ 